import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';

const OPEN_DELAY_MS = 150;
/** Long enough to cross the gap between the trigger and the panel. */
const CLOSE_DELAY_MS = 250;

type PointerHandlers = {
  onPointerEnter: (event: PointerEvent<HTMLElement>) => void;
  onPointerLeave: (event: PointerEvent<HTMLElement>) => void;
};

export type HoverOpen = {
  isHoverOpen: boolean;
  triggerHandlers: PointerHandlers;
  contentHandlers: PointerHandlers;
  /** Close now. `notify: false` skips `onHoverClose`, e.g. when a click pins the panel. */
  close: (options?: { notify?: boolean }) => void;
};

/**
 * Opens a popover while a mouse rests on its trigger or panel. Touch pointers
 * are ignored so a tap stays a click.
 */
export function useHoverOpen({
  isEnabled,
  onHoverClose,
}: {
  isEnabled: boolean;
  onHoverClose?: () => void;
}): HoverOpen {
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const isHoverOpenRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onHoverCloseRef = useRef(onHoverClose);
  useEffect(() => {
    onHoverCloseRef.current = onHoverClose;
  }, [onHoverClose]);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const setOpen = useCallback((next: boolean, notify = true) => {
    const wasOpen = isHoverOpenRef.current;
    isHoverOpenRef.current = next;
    setIsHoverOpen(next);
    if (wasOpen && !next && notify) onHoverCloseRef.current?.();
  }, []);

  const schedule = useCallback(
    (next: boolean, delayMs: number) => {
      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = undefined;
        setOpen(next);
      }, delayMs);
    },
    [clearTimer, setOpen]
  );

  useEffect(() => clearTimer, [clearTimer]);
  useEffect(() => {
    if (!isEnabled) {
      clearTimer();
      if (isHoverOpenRef.current) setOpen(false, false);
    }
  }, [clearTimer, isEnabled, setOpen]);

  const close = useCallback(
    (options?: { notify?: boolean }) => {
      clearTimer();
      setOpen(false, options?.notify ?? true);
    },
    [clearTimer, setOpen]
  );

  const scheduleClose = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (event.pointerType === 'touch' || !isHoverOpenRef.current) {
        clearTimer();
        return;
      }
      schedule(false, CLOSE_DELAY_MS);
    },
    [clearTimer, schedule]
  );

  const triggerHandlers = {
    onPointerEnter: (event: PointerEvent<HTMLElement>) => {
      if (!isEnabled || event.pointerType === 'touch') return;
      if (isHoverOpenRef.current) {
        clearTimer();
        return;
      }
      schedule(true, OPEN_DELAY_MS);
    },
    onPointerLeave: scheduleClose,
  };
  const contentHandlers = {
    onPointerEnter: (event: PointerEvent<HTMLElement>) => {
      if (event.pointerType !== 'touch' && isHoverOpenRef.current) clearTimer();
    },
    onPointerLeave: scheduleClose,
  };

  return {
    isHoverOpen: isEnabled && isHoverOpen,
    triggerHandlers,
    contentHandlers,
    close,
  };
}
